import { type Product, getEffectiveSaleMode, safeToFixed } from '@paulownia/shared/types';
import { Scale, Users } from 'lucide-react';

interface PriceDisplayProps {
    product: Product;
    compact?: boolean; // For ProductRowCard (smaller text)
    className?: string;
}

/**
 * Displays product price based on sale mode:
 * - UNIT: "12,50 €"
 * - UNIT_PRICED_BY_WEIGHT: "15 €/kg" with badges for weight/servings
 * - WEIGHT: "28 €/kg"
 * - WEIGHT_ESTIMATED: "32 €/kg" + "≈ 200g/pers"
 * - WEIGHT_PACKAGED: "À partir de 6,50 €" (minimum portion price)
 */
export function PriceDisplay({ product, compact = false, className = '' }: PriceDisplayProps) {
    const saleMode = getEffectiveSaleMode(product);
    const price = product.price;

    if (price === undefined || price === null) {
        return null;
    }

    const formatPrice = (value: number) => safeToFixed(value, 2).replace('.', ',');

    // Base text sizes based on compact mode
    const priceSize = compact ? 'text-base' : 'text-2xl';
    const unitSize = compact ? 'text-xs' : 'text-sm';

    switch (saleMode) {
        case 'UNIT':
            return (
                <div className={`text-right ${className}`}>
                    <span className={`font-bold text-gray-900 ${priceSize}`}>
                        {formatPrice(price)}€
                    </span>
                </div>
            );

        case 'UNIT_PRICED_BY_WEIGHT': {
            const avgWeight = product.weightEstimate?.averageWeight;
            const estimatedPrice = avgWeight ? price * avgWeight : null;
            const serves = product.weightEstimate?.servesPerUnit;

            // Compact view (ProductRowCard) - cleaner layout
            if (compact) {
                return (
                    <div className={`text-right ${className}`}>
                        <div>
                            <span className={`font-bold text-gray-900 ${priceSize}`}>
                                {formatPrice(price)}€
                            </span>
                            <span className={`text-gray-500 font-normal ml-1 ${unitSize}`}>/kg</span>
                        </div>
                        {avgWeight && estimatedPrice && (
                            <div className="flex items-center justify-end gap-1.5 mt-1">
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] font-medium">
                                    <Scale className="w-2.5 h-2.5" />
                                    ~{safeToFixed(avgWeight, 1).replace('.', ',')}kg
                                </span>
                                {serves && (
                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-orange-50 text-orange-600 rounded text-[10px] font-medium">
                                        <Users className="w-2.5 h-2.5" />
                                        {serves.min === serves.max ? serves.min : `${serves.min}-${serves.max}`}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                );
            }

            // Full view (ProductAddModal) - detailed card layout
            return (
                <div className={`${className}`}>
                    <div className="text-center">
                        <span className={`font-bold text-gray-900 ${priceSize}`}>
                            {formatPrice(price)}€
                        </span>
                        <span className={`text-gray-500 font-normal ml-1 ${unitSize}`}>/kg</span>
                    </div>
                    {avgWeight && estimatedPrice && (
                        <div className="mt-3 bg-gray-50 rounded-lg p-3">
                            <div className="flex items-center justify-center gap-3">
                                <div className="flex items-center gap-1.5 text-gray-600">
                                    <Scale className="w-4 h-4" />
                                    <span className="text-sm font-medium">~{safeToFixed(avgWeight, 1).replace('.', ',')} kg</span>
                                </div>
                                <div className="w-px h-4 bg-gray-300" />
                                <div className="text-sm font-semibold text-gray-900">
                                    ~{formatPrice(estimatedPrice)}€
                                </div>
                                {serves && (
                                    <>
                                        <div className="w-px h-4 bg-gray-300" />
                                        <div className="flex items-center gap-1.5 text-orange-600">
                                            <Users className="w-4 h-4" />
                                            <span className="text-sm font-medium">
                                                {serves.min === serves.max ? serves.min : `${serves.min}-${serves.max}`} pers
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        case 'WEIGHT':
            return (
                <div className={`text-right ${className}`}>
                    <span className={`font-bold text-gray-900 ${priceSize}`}>
                        {formatPrice(price)}€
                    </span>
                    <span className={`text-gray-500 font-normal ml-1 ${unitSize}`}>/kg</span>
                </div>
            );

        case 'WEIGHT_ESTIMATED': {
            const gramsPerPerson = product.servingEstimate?.gramsPerPerson;

            // Compact view
            if (compact) {
                return (
                    <div className={`text-right ${className}`}>
                        <div>
                            <span className={`font-bold text-gray-900 ${priceSize}`}>
                                {formatPrice(price)}€
                            </span>
                            <span className={`text-gray-500 font-normal ml-1 ${unitSize}`}>/kg</span>
                        </div>
                        {gramsPerPerson && (
                            <div className="flex items-center justify-end mt-1">
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-orange-50 text-orange-600 rounded text-[10px] font-medium">
                                    <Users className="w-2.5 h-2.5" />
                                    ~{gramsPerPerson}g/pers
                                </span>
                            </div>
                        )}
                    </div>
                );
            }

            // Full view
            return (
                <div className={`${className}`}>
                    <div className="text-center">
                        <span className={`font-bold text-gray-900 ${priceSize}`}>
                            {formatPrice(price)}€
                        </span>
                        <span className={`text-gray-500 font-normal ml-1 ${unitSize}`}>/kg</span>
                    </div>
                    {gramsPerPerson && (
                        <div className="mt-3 bg-orange-50 rounded-lg p-3">
                            <div className="flex items-center justify-center gap-2 text-orange-600">
                                <Users className="w-4 h-4" />
                                <span className="text-sm font-medium">~{gramsPerPerson}g par personne</span>
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        case 'WEIGHT_PACKAGED': {
            // Find minimum portion price
            const portions = product.portionSizes || [];
            // Filter to only valid numeric prices to prevent Math.min() from returning Infinity/NaN
            const validPrices = portions.map(p => p.price).filter(p => Number.isFinite(p));
            if (portions.length === 0 || validPrices.length === 0) {
                return (
                    <div className={`text-right ${className}`}>
                        <span className={`font-bold text-gray-900 ${priceSize}`}>
                            {formatPrice(price)}€
                        </span>
                        <span className={`text-gray-500 font-normal ml-1 ${unitSize}`}>/kg</span>
                    </div>
                );
            }

            const minPrice = Math.min(...validPrices);

            if (compact) {
                return (
                    <div className={`text-right ${className}`}>
                        <div className="text-gray-500 text-xs">À partir de</div>
                        <span className={`font-bold text-gray-900 ${priceSize}`}>
                            {formatPrice(minPrice)}€
                        </span>
                    </div>
                );
            }

            return (
                <div className={`text-center ${className}`}>
                    <div className="text-gray-500 text-sm">À partir de</div>
                    <span className={`font-bold text-gray-900 ${priceSize}`}>
                        {formatPrice(minPrice)}€
                    </span>
                </div>
            );
        }

        default:
            return (
                <div className={`text-right ${className}`}>
                    <span className={`font-bold text-gray-900 ${priceSize}`}>
                        {formatPrice(price)}€
                    </span>
                </div>
            );
    }
}
